import { useTheme } from '@mui/material/styles';
import { selectHideTilesInRestreaming } from 'features/layout/features/config/systemSessionSelectors';
import LayoutRoot from 'features/layout/LayoutRoot/LayoutRoot';
import { Box, GlobalStyles } from '@mui/material';
import SidePanelManager from 'features/side-panels/SidePanelManager';
import RoomLayoutWrapper from 'features/room/components/RoomLayoutWrapper';
import { useAudio } from 'hooks/useAudio';
import { useThemeMeta } from 'hooks/useThemeMeta';
import React, { useEffect, useRef } from 'react';
import joinSound from 'assets/sounds/join-room.wav';
import MediaPermissionRecoveryModal from 'features/room/modals/MediaPermissionRecoveryModal';
import RoomModalManager from 'features/room/RoomModalManager';
import { useTranslation } from 'react-i18next';
import { lazyNamespaces } from 'i18n';
import TopBar from 'features/topbar/components/TopBar';
import { SystemSessionGuard } from 'components/SystemSessionGuard';
import { useToolbarOrientation } from 'hooks/useToolbarOrientation';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectIsBroadcastSetupActive } from 'features/room/roomSlice';
import BroadcastSetup from 'features/room/broadcast-setup/BroadcastSetup';
import { roomRendered } from 'features/room/actions';
import { FeatureGuard } from 'features/feature-set/FeatureGuard';
import BreakoutsSplashScreenManager from 'features/breakout-rooms/components/splash-screen/BreakoutsSplashScreenManager';
import { ScreenshareRecorderRoot } from 'features/layout/LayoutRoot/ScreenshareRecorderRoot';
import RoomKeyboardShortcuts from 'features/room/components/RoomKeyboardShortcuts';
import { PhoneAudioStream } from 'features/sip/components/PhoneAudioStream';
import { TimerProvider } from 'features/timer/RoomTimerContext';
import WatermarkOverlay from 'features/watermark/WatermarkOverlay';

const bodyStyles = (
  <GlobalStyles
    styles={(theme) => ({
      body: {
        overflow: 'hidden',
        backgroundColor: theme.room.background,
      },
      '.tl-theme__light': {
        '--color-selected': `${theme.palette.primary.main}`,
        '--color-selected-contrast': `${theme.palette.primary.contrastText}`,
      },
    })}
  />
);

const Room = () => {
  const didInitRef = useRef(false);

  const { i18n } = useTranslation();

  const { audio } = useAudio();
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const isBroadcastSetupActive = useAppSelector(selectIsBroadcastSetupActive);

  const hideTilesInRestreaming = useAppSelector(selectHideTilesInRestreaming);

  useThemeMeta(theme.room.background);

  useToolbarOrientation();

  useEffect(() => {
    if (!didInitRef.current) {
      didInitRef.current = true;
      dispatch(roomRendered());
    }
  }, [dispatch]);

  useEffect(() => {
    i18n.loadNamespaces(lazyNamespaces);
  }, [i18n]);

  useEffect(() => {
    const playSound = () => {
      if (audio) {
        audio.src = joinSound;
        audio.play().catch(() => {
          // mute possible NotAllowedError on mobile devices
        });
      }
    };

    playSound();
  }, [audio]);

  const renderLayout = () => {
    if (hideTilesInRestreaming) {
      return <ScreenshareRecorderRoot />;
    }

    return <LayoutRoot />;
  };

  return (
    <TimerProvider>
      <Box
        sx={{
          position: 'relative',
          height: 'var(--app-height)',
          backgroundColor: theme.room.background,
        }}
      >
        {bodyStyles}
        <TopBar />
        <RoomLayoutWrapper>{renderLayout()}</RoomLayoutWrapper>
      </Box>
      <SystemSessionGuard>
        <>
          <RoomModalManager />
          <MediaPermissionRecoveryModal />
        </>
      </SystemSessionGuard>
      <SidePanelManager />
      {isBroadcastSetupActive && <BroadcastSetup />}
      <FeatureGuard feature="breakoutRooms">
        <BreakoutsSplashScreenManager />
      </FeatureGuard>
      <RoomKeyboardShortcuts />
      <PhoneAudioStream />
      <WatermarkOverlay />
    </TimerProvider>
  );
};

export default Room;
